import { filterBy } from "@progress/kendo-data-query";
import { DatePicker } from "@progress/kendo-react-dateinputs";
import {
  MultiSelect,
  DropDownList,
  MultiSelectFilterChangeEvent,
  ListItemProps,
  DropDownListFilterChangeEvent,
  DropDownListChangeEvent,
  ComboBox,
  ComboBoxFilterChangeEvent,
  ComboBoxFocusEvent,
  DropDownListHandle,
} from "@progress/kendo-react-dropdowns";
import {
  FieldWrapper,
  FieldRenderProps,
  FormRenderProps,
} from "@progress/kendo-react-form";
import {
  Checkbox,
  Input,
  RadioGroup,
  Switch,
  TextArea,
  CheckboxChangeEvent,
  NumericTextBox,
  TextBox,
  RadioButton,
} from "@progress/kendo-react-inputs";
import { Label, Hint, Error } from "@progress/kendo-react-labels";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { AxiosError } from "axios";
import isEqual from "lodash-es/isEqual";
import isString from "lodash-es/isString";
import last from "lodash-es/last";
import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { If, Then } from "react-if";
import TagsInput from "react-tagsinput";
import useDeepCompareEffect, {
  useDeepCompareMemoize,
} from "use-deep-compare-effect";
import { TlxHttpResponse } from "@/api";
import CopyToClipboard from "@/components/CopyToClipboard";
import Errors from "@/components/Dialog/Error";
import { renderTooltipText } from "@/components/Kendo/ToolTip/renderTooltipText";
import TLXMultiSelect from "@/components/MultiSelect";
import MultiSelectTabs from "@/components/MultiSelectTabs";
import { itemComboBoxRender } from "./FinalForm";
import FormLabel from "./FormLabel";
import { default as FormSelectItemRenderComponent } from "./FormSelectItemRender";
import FormSelectValueRender from "./FormSelectValueRender";
import IconHint from "./IconHint";
import ListNoData from "./ListNoData";
import "./style.scss";

export type SelectData = {
  checked?: boolean;
  name: string | undefined;
  id?: string | number | string[] | undefined;
  disabled?: boolean;
  checkAllDisabled?: boolean;
  custom?: string;
  groupField?: string;
  title?: string;
};

type CheckboxOption<TValue = unknown> = {
  label: string;
  value: TValue;
  isDisabled?: boolean;
  isChecked?: boolean;
};

type RadioOption<TValue = unknown> = CheckboxOption<TValue> & {
  hint?: string;
  disabled?: boolean;
};

type FormKendoMultiSelectProps = Partial<FieldRenderProps> & {
  data?: SelectData[];
  value?: SelectData[];
  defaultValue?: SelectData[];
};

interface IFormDropDownListProps extends Partial<FieldRenderProps> {
  apiError?: AxiosError<TlxHttpResponse>;
  filterByProperty?: string;
  data?: SelectData[];
  optional?: boolean;
  /**
   * Optional function to render an image or icon for each dropdown item.
   *
   * This is called when rendering the list of items, with the corresponding
   * `dataItem` for that row. It should return a React node (e.g. an <img>,
   * icon component, or any JSX) that will be displayed alongside the item text.
   */
  imageRender?: (dataItem: ListItemProps["dataItem"]) => React.ReactNode;
}

type ComboBoxItem = Record<string, unknown>;

export const FormTextBox = (fieldRenderProps: FieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    type,
    optional,
    hintIcon = false,
    defaultShowValidationMessage = false,
    ...others
  } = fieldRenderProps;

  const inputId = id || fieldRenderProps.name;
  const showValidationMessage =
    (defaultShowValidationMessage || touched) && validationMessage;
  const showHint = hint;
  const hindId = showHint ? `${inputId}_hint` : "";
  const errorId = showValidationMessage ? `${inputId}_error` : "";
  const labelId = label ? `${inputId}_label` : "";

  return (
    <FieldWrapper>
      <FormLabel
        id={labelId}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
        floating={others.floatingLabel}
        floatLabel={others.floatLabel}
        label={label}
        value={others.value?.toString().length ? others.value : null}
        placeholder={others.placeholder}
        optional={optional}
      >
        <div className={`k-form-field-wrap ${inputId}`}>
          <TextBox
            valid={valid}
            type={type}
            id={inputId}
            disabled={disabled}
            autoComplete="off"
            style={{ paddingRight: 20 }}
            {...others}
          />
          {showHint && !hintIcon && <Hint id={hindId}>{hint}</Hint>}
          {showHint && hintIcon && <IconHint hint={hint} iconWidth={14} />}
          {showValidationMessage && (
            <Error id={errorId}>{validationMessage}</Error>
          )}
        </div>
      </FormLabel>
    </FieldWrapper>
  );
};

export const FormInput = (fieldRenderProps: FieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    type,
    optional,
    hintIcon = false,
    copyToText, // true to enable copy to clipboard icon
    defaultShowValidationMessage = false,
    ...others
  } = fieldRenderProps;

  const inputId = id || fieldRenderProps.name;
  const showValidationMessage =
    (defaultShowValidationMessage || touched) && validationMessage;
  const showHint = hint;
  const hindId = showHint ? `${inputId}_hint` : "";
  const errorId = showValidationMessage ? `${inputId}_error` : "";
  const labelId = label ? `${inputId}_label` : "";

  const showHintText = showHint && !hintIcon;
  const showHintIcon = showHint && hintIcon;

  return (
    <FieldWrapper>
      <Tooltip
        anchorElement="target"
        parentTitle={true}
        style={{ maxWidth: 400 }}
        showCallout={false}
        content={renderTooltipText}
      >
        <FormLabel
          id={labelId}
          editorId={id}
          editorValid={valid}
          editorDisabled={disabled}
          floating={others.floatingLabel}
          floatLabel={others.floatLabel}
          label={label}
          value={others.value?.toString().length ? others.value : null}
          placeholder={others.placeholder}
          optional={optional}
        >
          <div className={`k-form-field-wrap ${inputId}`}>
            <Input
              valid={valid}
              type={type}
              id={inputId}
              disabled={disabled}
              autoComplete="off"
              ariaDescribedBy={`${hindId} ${errorId}`}
              style={{ paddingRight: 20 }}
              onKeyDown={e => {
                // Prevents the default action of the Enter key
                if (e.key === "Enter") {
                  e.preventDefault();
                }
              }}
              {...others}
            />
            {showHintText && <Hint id={hindId}>{hint}</Hint>}
            {showHintIcon && <IconHint hint={hint} />}
            <If condition={copyToText}>
              <Then>
                <span
                  role="button"
                  style={{
                    position: "absolute",
                    // Same 5px separation as the RHF FormInput: the info icon's
                    // left edge sits 29px from the field edge (right:10 +
                    // marginRight:5 + a 14px icon), so 34 clears it (QA-28).
                    // This legacy field is the one the Integration ID uses.
                    // 15px from the trailing edge when it stands alone (matching the
                    // field's own inset); 34px when it must clear the info icon.
                    right: showHintIcon ? "2.125rem" : "0.9375rem",
                    // Half the 46px control, NOT 50% — `.k-form-field-wrap` also holds
                    // <Hint> and <Error>, so a percentage drops this icon below the
                    // input once a validation error renders. Matches IconHint.
                    top: "1.4375rem",
                    transform: "translateY(-50%)",
                    // Box matches the 14px icon exactly (and the info icon's
                    // box), so the measured 5px gap is also the *visual* gap —
                    // a 20px box around a 14px glyph made them look adjacent.
                    width: "0.875rem",
                    height: "0.875rem",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    color: "var(--tlx-cta-primary-default)",
                  }}
                >
                  <CopyToClipboard copyToText={others.value} />
                </span>
              </Then>
            </If>

            {showValidationMessage && (
              <Error id={errorId}>{validationMessage}</Error>
            )}
          </div>
        </FormLabel>
      </Tooltip>
    </FieldWrapper>
  );
};

export const FormPassword = (fieldRenderProps: FieldRenderProps) => {
  return <FormInput {...fieldRenderProps} type="password" />;
};

export const FormFileInput = (fieldRenderProps: FieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    optional,
    hintIcon = false,
    defaultShowValidationMessage = false,
    value,
    processValue = false,
    onChange,
    ...others
  } = fieldRenderProps;

  const fileName: string | undefined = value
    ? last((isString(value) ? value : value.name).split("\\"))
    : undefined;

  const inputId = id || fieldRenderProps.name;
  const showValidationMessage =
    (defaultShowValidationMessage || touched) && validationMessage;
  const showHint = hint;
  const hindId = showHint ? `${inputId}_hint` : "";
  const errorId = showValidationMessage ? `${inputId}_error` : "";
  const labelId = label ? `${inputId}_label` : "";
  const fileInputClassname = "tlx-file-input";

  const handleFileChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (processValue) {
        const nextValue = event.target.files?.[0];
        onChange?.({ value: nextValue });
        return;
      }
      onChange?.(event);
    },
    [onChange, processValue],
  );

  return (
    <FieldWrapper className="tlx-file-input-field-wrapper">
      <Tooltip
        anchorElement="target"
        parentTitle={true}
        style={{ maxWidth: 400 }}
        showCallout={false}
        content={renderTooltipText}
      >
        <FormLabel
          id={labelId}
          editorId={id}
          editorValid={valid}
          editorDisabled={disabled}
          floating={others.floatingLabel}
          floatLabel={others.floatLabel}
          label={label}
          value={fileName?.toString().length ? fileName : undefined}
          placeholder={others.placeholder}
          optional={optional}
        >
          <div className={`k-form-field-wrap ${inputId}`}>
            <Input
              valid={valid}
              disabled
              autoComplete="off"
              ariaDescribedBy={`${hindId} ${errorId}`}
              style={{ paddingRight: 20 }}
              value={fileName}
            />

            <div className="tlx-file-input-wrapper">
              <>
                {fileName ? (
                  <span
                    className="tlx-file-input-clear k-icon k-font-icon k-i-close-outline"
                    onClick={() => {
                      const input = document.querySelector(
                        `input.${fileInputClassname}#${inputId}`,
                      );

                      if (input instanceof HTMLInputElement) {
                        input.value = "";
                      }

                      onChange?.({
                        value: null,
                        target: {
                          files: [],
                        },
                      });
                    }}
                  />
                ) : (
                  <label className="tlx-file-input-label" htmlFor={inputId}>
                    <span className="k-icon k-font-icon k-i-upload" /> Upload
                  </label>
                )}

                <input
                  hidden
                  id={inputId}
                  className={fileInputClassname}
                  disabled={disabled}
                  onChange={handleFileChange}
                  {...others}
                  type="file"
                />
              </>
            </div>

            {showHint && !hintIcon && <Hint id={hindId}>{hint}</Hint>}
            {showHint && hintIcon && <IconHint hint={hint} iconWidth={14} />}
            {showValidationMessage && (
              <Error id={errorId}>{validationMessage}</Error>
            )}
          </div>
        </FormLabel>
      </Tooltip>
    </FieldWrapper>
  );
};

export const FormTextArea = (fieldRenderProps: FieldRenderProps) => {
  const {
    validationMessage,
    touched,
    label,
    id,
    valid,
    disabled,
    hint,
    optional,
    ...others
  } = fieldRenderProps;

  const inputId = id || fieldRenderProps.name;
  const showValidationMessage = touched && validationMessage;
  const showHint = hint;
  const hindId = showHint ? `${inputId}_hint` : "";
  const errorId = showValidationMessage ? `${inputId}_error` : "";

  return (
    <FieldWrapper>
      <FormLabel
        id={inputId}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
        floating={others.floatingLabel}
        floatLabel={others.floatLabel}
        label={label}
        value={others.value?.length ? others.value : null}
        placeholder={others.placeholder}
        optional={optional}
      >
        <div className={"k-form-field-wrap"}>
          <TextArea
            valid={valid}
            id={id}
            disabled={disabled}
            autoComplete="off"
            ariaDescribedBy={`${hindId} ${errorId}`}
            {...others}
          />
          {showHint && <Hint id={hindId}>{hint}</Hint>}
          {showValidationMessage && (
            <Error id={errorId}>{validationMessage}</Error>
          )}
        </div>
      </FormLabel>
    </FieldWrapper>
  );
};

export const FormRadioGroup = (fieldRenderProps: FieldRenderProps) => {
  const {
    validationMessage,
    touched,
    id,
    label,
    layout,
    valid,
    disabled,
    hint,
    value,
    ...others
  } = fieldRenderProps;

  const showValidationMessage = touched && validationMessage;
  const showHint = hint;
  const hindId = showHint ? `${id}_hint` : "";
  const errorId = showValidationMessage ? `${id}_error` : "";
  const labelId = label ? `${id}_label` : "";

  return (
    <FieldWrapper>
      <Label
        id={labelId}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {label}
      </Label>
      <RadioGroup
        id={id}
        ariaDescribedBy={`${hindId} ${errorId}`}
        ariaLabelledBy={labelId}
        valid={valid}
        disabled={disabled}
        layout={layout}
        defaultValue={value}
        value={value}
        {...others}
      />
      {showHint && <Hint id={hindId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  );
};

export const CheckboxGroup = (fieldRenderProps: FieldRenderProps) => {
  const {
    validationMessage,
    touched,
    id,
    label,
    valid,
    disabled,
    hint,
    data,
    name,
    others,
    value,
    onChange,
  } = fieldRenderProps;

  const showValidationMessage = touched && validationMessage;
  const showHint = hint;
  const hindId = showHint ? `${id}_hint` : "";
  const errorId = showValidationMessage ? `${id}_error` : "";
  const labelId = label ? `${id}_label` : "";

  const options = (data as CheckboxOption[]) || [];

  const [values, setValues] = React.useState<CheckboxOption["value"][]>(
    value || [],
  );

  const onValueChange = React.useCallback(
    (event: CheckboxChangeEvent, item: CheckboxOption) => {
      const isChecked = event.syntheticEvent.currentTarget.checked;

      let newValues = [...values];
      if (isChecked) {
        newValues.push(item.value);
      } else {
        newValues = newValues.filter(val => item.value !== val);
      }
      setValues(newValues);
      onChange({ value: newValues });
    },
    [onChange, values],
  );

  return (
    <FieldWrapper>
      <Label
        id={labelId}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {label}
      </Label>

      <ul
        className="k-radio-list k-list-horizontal"
        aria-describedby={`${hindId} ${errorId}`}
        dir="ltr"
      >
        {options.map((item, index) => (
          <li className="k-radio-item" key={index}>
            <Checkbox
              label={item.label}
              name={name}
              valid={valid}
              defaultChecked={
                value?.find((val: SelectData) => val === item.value) !==
                undefined
              }
              onChange={event => onValueChange(event, item)}
              disabled={item.isDisabled /* disabled */}
              checked={item.isChecked}
              {...others}
            />
          </li>
        ))}
      </ul>

      {showHint && <Hint id={hindId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  );
};

export const FromRadioCustomGroup = (fieldRenderProps: FieldRenderProps) => {
  const {
    validationMessage,
    touched,
    id,
    label,
    valid,
    disabled,
    hint,
    data,
    name,
    others,
    value,
    onChange,
    layout = "vertical",
  } = fieldRenderProps;

  const showValidationMessage = touched && validationMessage;
  const showHint = hint;
  const hindId = showHint ? `${id}_hint` : "";
  const errorId = showValidationMessage ? `${id}_error` : "";
  const labelId = label ? `${id}_label` : "";

  const options = (data as RadioOption[]) || [];

  const onValueChange = React.useCallback(
    (event: CheckboxChangeEvent, item: RadioOption) => {
      const isChecked = event.syntheticEvent.currentTarget.checked;
      onChange({ value: isChecked ? item.value : null });
    },
    [onChange],
  );

  return (
    <FieldWrapper>
      <Label
        id={labelId}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
      >
        {label}
      </Label>

      <ul
        className={`k-radio-list k-list-${layout}`}
        aria-describedby={`${hindId} ${errorId}`}
        dir="ltr"
        style={{ marginTop: 5 }}
      >
        {options.map((item, index) => (
          <li
            key={index}
            className="k-radio-list-item"
            style={{ alignItems: "center" }}
          >
            <div
              className={`k-radio-list-item ${
                item.disabled || disabled ? "k-disabled" : ""
              }`}
              style={{ marginTop: -5 }}
            >
              <RadioButton
                label={item.label}
                name={name}
                valid={valid}
                onChange={event => onValueChange(event, item)}
                disabled={item.isDisabled /* disabled */ || disabled}
                checked={item.value === value}
                {...others}
              />
            </div>
            {item.hint && (
              <IconHint
                hint={item.hint}
                iconWidth={14}
                style={{ position: "unset", verticalAlign: "middle" }}
              />
            )}
          </li>
        ))}
      </ul>

      {showHint && <Hint id={hindId}>{hint}</Hint>}
      {showValidationMessage && <Error id={errorId}>{validationMessage}</Error>}
    </FieldWrapper>
  );
};

export const FormSelectItemRender = FormSelectItemRenderComponent;

export const FormKendoMultiSelect: React.FC<
  FormKendoMultiSelectProps
> = fieldRenderProps => {
  const {
    validationMessage,
    touched,
    id = fieldRenderProps.name || "label",
    label,
    valid,
    disabled,
    hint,
    data = [],
    noDataMessage,
    floatingLabel,
    floatLabel,
    placeholder,
    popupWidth,
    itemRender,
    hintIcon,
    valueSeparator, // separator to add values to separate with a separator by typing without selecting from the dropdown or enter key // allow user to copy values with this separator and paste it
    value: formValue,
    allowCustom,
    onChange,
    ...others
  } = fieldRenderProps;

  const normalizeValue = useCallback(
    (inputValue: unknown): SelectData[] => {
      let normalizedInputValue = inputValue;
      while (
        normalizedInputValue &&
        typeof normalizedInputValue === "object" &&
        "value" in (normalizedInputValue as Record<string, unknown>)
      ) {
        normalizedInputValue = (normalizedInputValue as { value?: unknown })
          .value;
      }

      if (Array.isArray(normalizedInputValue)) {
        return normalizedInputValue as SelectData[];
      }
      if (!isString(normalizedInputValue)) {
        return [];
      }

      const parsedValues = valueSeparator
        ? normalizedInputValue.split(valueSeparator)
        : [normalizedInputValue];

      return parsedValues
        .map(item => item.trim())
        .filter(Boolean)
        .map(name => ({ name }));
    },
    [valueSeparator],
  );

  const showValidationMessage = touched && validationMessage;
  const showHint = hint;
  const hindId = showHint ? `${id}_hint` : "";
  const errorId = showValidationMessage ? `${id}_error` : "";
  const labelId = label ? `${id}_label` : "";
  const [dropDownData, setDropDownData] = React.useState<SelectData[]>([]);
  const [filterValue, setFilterValue] = React.useState<string>("");
  const [value, setValue] = React.useState<SelectData[]>(
    normalizeValue(formValue),
  );

  React.useEffect(() => {
    setValue(normalizeValue(formValue));
  }, [formValue, normalizeValue]);

  const checkAll = useCallback(
    (name: string) =>
      name?.toLowerCase() === "all" || name?.toLowerCase().startsWith("all_"),
    [],
  );

  const newData = useMemo(() => {
    const hasAll = isString(value)
      ? undefined
      : value?.find(item => checkAll(item.name!))?.name !== undefined;

    return [...data].map(item => ({
      ...item,
      checkAllDisabled: hasAll
        ? !checkAll(item.name!)
        : value?.length > 0
          ? checkAll(item.name!)
          : false,
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [useDeepCompareMemoize(data), value, checkAll]);

  // currently we have allowCustom is true all places we use this component
  // TODO: onChange handler in the useEffect should be handled proper way others.onChange?.({ value: newValues }); => others.onChange?.(newValues);
  useDeepCompareEffect(() => {
    // if (
    //   newData?.length > 0 &&
    //   !others.loading &&
    //   value &&
    //   !others.allowCustom
    // ) {
    //   // removing selected options which are not present in the options
    //   const newValues = compact(
    //     value.map(item =>
    //       newData.find((dataItem: SelectData) => {
    //         const idAttr = (others.dataItemKey as keyof SelectData) || "id";
    //         return dataItem[idAttr] === item[idAttr];
    //       }),
    //     ),
    //   );
    //   if (newValues.length) setValue(newValues);
    //   others.onChange?.({ value: newValues });
    // }
    setDropDownData(newData);
  }, [newData, value, others.loading]);

  const filterChange = useCallback(
    (event: MultiSelectFilterChangeEvent) => {
      let newFilterValue = event.filter.value || "";

      // allowing to specify a separator which will act like enter key
      // if the value has a separator, it will split the value and add it to the value array
      // and keep the value of after separator in the filter only
      if (valueSeparator && allowCustom) {
        if (event.filter.value.includes(valueSeparator)) {
          const values = event.filter.value.split(valueSeparator);
          const lastValue = values.pop().trim();
          newFilterValue = lastValue || "";

          // removed empty items
          const removedEmptyValues = values.filter(
            (item: string) => item.trim().length,
          );

          const normalizedValue = normalizeValue(value);
          const newValues = [
            ...normalizedValue,
            ...removedEmptyValues.map((name: string) => ({ name })),
          ];
          onChange?.({
            target: { value: newValues },
            value: newValues,
          });
        }
      }

      setFilterValue(newFilterValue.trim());
      setDropDownData(filterBy([...newData], event.filter));
    },
    [valueSeparator, allowCustom, onChange, value, newData, normalizeValue],
  );

  if (!data) return null;

  return (
    <FieldWrapper>
      <FormLabel
        id={labelId}
        editorId={id}
        editorValid={valid!}
        editorDisabled={disabled}
        floating={floatingLabel}
        floatLabel={floatLabel}
        label={label}
        value={formValue?.length ? formValue : null}
        placeholder={placeholder}
      >
        <MultiSelect
          ariaDescribedBy={`${hindId} ${errorId}`}
          ariaLabelledBy={labelId}
          valid={valid}
          disabled={disabled}
          textField="name"
          defaultValue={value}
          filterable={true}
          dataItemKey="id"
          itemRender={itemRender || FormSelectItemRender}
          onFilterChange={filterChange}
          filter={filterValue}
          data={dropDownData}
          placeholder={placeholder}
          popupSettings={
            popupWidth
              ? { width: popupWidth }
              : { ...(others.popupSettings ?? {}) }
          }
          listNoDataRender={() => (
            <ListNoData message={noDataMessage} loading={others.loading} />
          )}
          allowCustom={allowCustom}
          onChange={onChange}
          {...others}
          value={value}
        />
        {showHint && !hintIcon && <Hint id={hindId}>{hint}</Hint>}
        {showHint && hintIcon && <IconHint hint={hint} style={{ right: 18 }} />}
        {showValidationMessage && (
          <Error id={errorId}>{validationMessage}</Error>
        )}
      </FormLabel>
    </FieldWrapper>
  );
};

export const FormMultiSelect = TLXMultiSelect;

/**
 * Synchronizes the value of a dropdown field with its dependent field if the dependent field is missing.
 *
 * This function checks if the current value object of the dropdown has all the required fields. If the dependent field
 * (`requiredItemKey`) is not present, it attempts to find the corresponding full object from the provided `data` array
 * using the `dataItemKey`. Once found, it updates the value object by setting the dependent field to the appropriate value.
 *
 * @param onChange - A callback function to update the form field values.
 * @param data - An array of objects representing the dropdown options.
 * @param value - The current value object of the dropdown.
 * @param name - The name of the field to be passed to the `onChange` function.
 * @param requiredItemKey - The name of the dependent field to be updated if missing.
 * @param dataItemKey - The key used to match the current value object with an item in the `data` array.
 */
export const useSyncDropdownValueData = ({
  form,
  data,
  name,
  requiredItemKey,
  dataItemKey,
  enabled = true,
}: {
  form: FormRenderProps;
  data: Record<string, unknown>[];
  name: string;
  requiredItemKey: string[];
  dataItemKey: string;
  enabled?: boolean;
}): void => {
  const { onChange, valueGetter } = form;
  const value = valueGetter(name);

  useDeepCompareEffect(() => {
    if (
      enabled &&
      Array.isArray(data) &&
      data.length > 0 &&
      value?.[dataItemKey] &&
      !requiredItemKey.every(item => value?.[item] && value[item] !== undefined)
    ) {
      const matchedItem = data.find(
        item => item[dataItemKey] === value[dataItemKey],
      );

      if (
        matchedItem &&
        requiredItemKey.every(
          item => matchedItem?.[item] && matchedItem[item] !== undefined,
        ) &&
        !isEqual(matchedItem, value)
      ) {
        onChange(name, { value: matchedItem });
      }
    }
  }, [data, value, enabled]);
};

export const FormDropDownList = <
  TValue extends SelectData | null = SelectData | null,
>(
  fieldRenderProps: IFormDropDownListProps,
) => {
  const {
    validationMessage,
    touched,
    id = fieldRenderProps.name,
    label,
    valid,
    disabled,
    hint,
    noDataMessage,
    fillMode,
    floatingLabel,
    floatLabel,
    placeholder,
    FieldWrapperClassName,
    data,
    filterable = true,
    popupWidth,
    itemRender,
    onChange,
    value: formValue,
    defaultValue,
    hintIcon,
    animate = true,
    textField = "name",
    dataItemKey = "id",
    disableAutoClear,
    apiError,
    descriptionField,
    filterByProperty,
    showError,
    imageRender,
    valueRender: _valueRender,
    optional = false,
    ...others
  } = fieldRenderProps;
  const showValidationMessage = (showError || touched) && validationMessage;
  const showApiError = !others.loading && apiError;

  const [value, setValue] = useState<TValue | null | undefined>(
    formValue ?? defaultValue,
  );

  const [ulWidth, setUlWidth] = useState<number | undefined>();
  const dropDownRef = React.useRef<DropDownListHandle>(null);

  useEffect(() => {
    setValue(formValue || defaultValue);
  }, [formValue, defaultValue]);

  const onInputChange = useCallback(
    (event: DropDownListChangeEvent) => {
      const nextValue = event.value as TValue;
      setValue(nextValue);
      onChange?.({
        value: nextValue,
        //@ts-expect-error - TODO: fix this
        syntheticEvent: event.syntheticEvent,
      });
    },
    [onChange],
  );

  const showHint = hint;
  const [dropDownData, setDropDownData] = React.useState<SelectData[]>(
    data || [],
  );
  const dataSource = data || [];
  const hindId = showHint ? `${id}_hint` : "";
  const errorId = showValidationMessage ? `${id}_error` : "";
  const labelId = `${id}_label`;
  if (value || others.value) {
    delete others.defaultItem;
  }

  useDeepCompareEffect(() => {
    setDropDownData(dataSource);
  }, [dataSource]);

  useEffect(() => {
    if (defaultValue) {
      onChange?.({ value: defaultValue });
    }
    // only need for first render
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setUlWidth(dropDownRef.current?.element?.getBoundingClientRect().width);
  }, [dropDownRef]);

  useDeepCompareEffect(() => {
    if (
      !disableAutoClear &&
      dataSource.length > 0 &&
      !others.loading &&
      value &&
      !others.allowCustom
    ) {
      const currentValue = value as unknown as SelectData;
      const newValue =
        dataSource.find((dataItem: SelectData) => {
          const idAttr = dataItemKey as keyof SelectData;
          return dataItem[idAttr] === currentValue[idAttr];
        }) || null;

      if (!newValue) {
        setValue(newValue as TValue);
        onChange?.({ value: newValue as TValue });
      }
    }
  }, [
    dataSource,
    value,
    others.loading,
    disableAutoClear,
    dataItemKey,
    others.allowCustom,
    onChange,
  ]);

  const filterChange = (event: DropDownListFilterChangeEvent) => {
    if (filterByProperty) {
      setDropDownData(
        filterBy([...dataSource], {
          logic: "or",
          filters: [event.filter, { ...event.filter, field: filterByProperty }],
        }),
      );
      return;
    }
    setDropDownData(filterBy([...dataSource], event.filter));
  };

  return (
    <FieldWrapper className={FieldWrapperClassName}>
      <FormLabel
        id={labelId}
        editorId={id}
        editorValid={!!valid}
        editorDisabled={disabled}
        floating={floatingLabel}
        label={label}
        value={formValue}
        floatLabel={floatLabel}
        placeholder={placeholder}
        optional={optional}
      >
        <DropDownList
          ariaDescribedBy={`${hindId} ${errorId}`}
          ref={dropDownRef}
          ariaLabelledBy={labelId}
          textField={textField}
          dataItemKey={dataItemKey}
          valid={valid}
          value={value ?? null}
          disabled={disabled}
          filterable={filterable}
          style={{ width: "100%" }}
          fillMode={fillMode}
          data={dropDownData}
          onFilterChange={filterChange}
          onChange={onInputChange}
          skipDisabledItems={true}
          valueRender={
            _valueRender
              ? _valueRender
              : (element, value) =>
                  FormSelectValueRender(element, textField, value, imageRender)
          }
          itemRender={
            itemRender
              ? (
                  li: React.ReactElement<HTMLLIElement>,
                  itemProps: ListItemProps,
                ) => itemRender(li, { ...itemProps, ulWidth })
              : (li, itemProps) =>
                  FormSelectItemRender(li, {
                    ...itemProps,
                    descriptionField,
                    imageRender,
                  })
          }
          id={`${id}_input`}
          popupSettings={
            popupWidth ? { width: popupWidth, animate } : { animate }
          }
          listNoDataRender={() => (
            <ListNoData message={noDataMessage} loading={others.loading} />
          )}
          {...others}
        />
        {showHint && !hintIcon && <Hint id={hindId}>{hint}</Hint>}
        {showHint && hintIcon && <IconHint hint={hint} />}
        {showValidationMessage && (
          <Error id={errorId}>{validationMessage}</Error>
        )}
        {showApiError && <Errors errors={[apiError]} />}
      </FormLabel>
    </FieldWrapper>
  );
};

export const FormSwitch: React.FC<FieldRenderProps> = fieldRenderProps => {
  const {
    validationMessage,
    visited,
    value,
    onLabel,
    offLabel,
    onFocus,
    onBlur,
    defaultChecked,
    size,
    id,
    ...others
  } = fieldRenderProps;

  return (
    <div onFocus={onFocus} onBlur={onBlur}>
      <Switch
        checked={value}
        id={id}
        size={size}
        onLabel={onLabel}
        offLabel={offLabel}
        defaultChecked={defaultChecked}
        {...others}
      />
      {visited && validationMessage && (
        <div className={"k-required"}>{validationMessage}</div>
      )}
    </div>
  );
};

// kendo combobox input component with custom and filter enabled
export const FormComboBox: React.FC<FieldRenderProps> = props => {
  const {
    data = [],
    customLabel = "Custom",
    value,
    dataItemKey = "id",
    textField = "name",
    onChange,
    loading,
    label,
    id = props.name,
    touched,
    validationMessage,
    valid,
    disabled,
    floatingLabel,
    floatLabel,
    placeholder,
    hint,
    hintIcon,
    ...others
  } = props;

  const showValidationMessage = touched && validationMessage;
  const errorId = `${id}_error`;
  const labelId = `${id}_label`;
  const showHint = hint;
  const hindId = showHint ? `${id}_hint` : "";
  const [dropDownData, setDropDownData] = React.useState<SelectData[]>([]);
  const newData: SelectData[] = React.useMemo(() => {
    return (data as ComboBoxItem[]).map(item => {
      const innerItem = {
        name: textField ? (item[textField] as string) : (item.name as string),
        id: (dataItemKey ? item[dataItemKey] : item.id) as SelectData["id"],
        title: item.title as string | undefined,
      };
      return { ...item, ...innerItem };
    });

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataItemKey, textField, useDeepCompareMemoize(data)]);

  useDeepCompareEffect(() => {
    setDropDownData(newData);
  }, [newData]);

  const filterChange = (event: ComboBoxFilterChangeEvent) => {
    const { filter } = event;

    const items = filterBy([...newData], filter);
    if (
      filter.value.length > 0 &&
      !items.find(item => item.name === filter.value)
    ) {
      items.unshift({
        id: filter.value,
        name: `${filter.value}`,
        title: `${filter.value}`,
        custom: customLabel,
      });
    }
    setDropDownData(items);
  };

  const onComboBoxFocus = useCallback((event: ComboBoxFocusEvent) => {
    if (!event.target.state.opened) {
      event.target.setState({ opened: true });
    }
  }, []);

  return (
    <FieldWrapper>
      <FormLabel
        id={labelId}
        editorId={id}
        editorValid={!!valid}
        editorDisabled={disabled}
        floating={floatingLabel}
        label={label}
        value={value?.[dataItemKey]}
        floatLabel={floatLabel}
        placeholder={placeholder}
      >
        <ComboBox
          value={value}
          textField={textField}
          dataItemKey={dataItemKey}
          filterable
          allowCustom
          onFilterChange={filterChange}
          itemRender={itemComboBoxRender}
          data={dropDownData}
          onChange={onChange}
          loading={loading}
          id={id}
          valid={valid}
          disabled={disabled}
          {...others}
          onFocus={onComboBoxFocus}
        />

        {showHint && !hintIcon && <Hint id={hindId}>{hint}</Hint>}
        {showHint && hintIcon && (
          <IconHint hint={hint} style={{ right: "3.125rem" }} />
        )}
        {showValidationMessage && (
          <Error id={errorId}>{validationMessage}</Error>
        )}
      </FormLabel>
    </FieldWrapper>
  );
};

export const FormTagsInput: React.FC<FieldRenderProps> = fieldRenderProps => {
  const {
    validationMessage,
    value,
    onChange,
    floatingLabel,
    floatLabel,
    placeholder,
    label,
    name,
    id = name,
    valid,
    disabled,
    modified,
    hint,
    optional,
  } = fieldRenderProps;
  const showValidationMessage = modified && validationMessage;

  const hindId = `${id}_hint`;
  const labelId = `${id}_label`;
  const errorId = showValidationMessage ? `${id}_error` : "";
  const onInputChange = useCallback(
    (tags: string[]) => {
      onChange({
        value: tags,
      });
    },
    [onChange],
  );

  return (
    <FieldWrapper>
      <FormLabel
        id={labelId}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
        floating={floatingLabel}
        floatLabel={floatLabel}
        label={label}
        value={value?.length ? value : null}
        placeholder={placeholder}
        optional={optional}
      >
        <div
          onKeyDown={(e: React.KeyboardEvent<HTMLDivElement>) => {
            e.stopPropagation();
          }}
        >
          <TagsInput
            onChange={onInputChange}
            value={value || []}
            disabled={disabled}
            onlyUnique={true}
            addOnBlur={true}
            inputProps={{
              className: "react-tagsinput-input",
              placeholder: "",
            }}
          />
        </div>
        {hint && <Hint id={hindId}>{hint}</Hint>}
        {showValidationMessage && (
          <Error id={errorId}>{validationMessage}</Error>
        )}
      </FormLabel>
    </FieldWrapper>
  );
};

export const FormDatePicker: React.FC<FieldRenderProps> = fieldRenderProps => {
  const {
    validationMessage,
    floatingLabel,
    floatLabel,
    placeholder,
    label,
    id = label,
    valid,
    disabled,
    touched,
    hint,
    name,
    optional,
    ...others
  } = fieldRenderProps;
  const showValidationMessage = touched && validationMessage;
  const hindId = `${id}_hint`;
  const labelId = label ? `${id}_label` : "";
  const errorId = showValidationMessage ? `${id}_error` : "";
  return (
    <FieldWrapper>
      <FormLabel
        id={labelId}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
        floating={floatingLabel}
        floatLabel={floatLabel}
        label={label}
        value={others.value?.toString() || "blank"}
        placeholder={placeholder}
        optional={optional}
      >
        <DatePicker disabled={disabled} name={name} {...others} />
        {hint && <Hint id={hindId}>{hint}</Hint>}
        {showValidationMessage && (
          <Error id={errorId}>{validationMessage}</Error>
        )}
      </FormLabel>
    </FieldWrapper>
  );
};

export const FormNumericTextBox: React.FC<
  FieldRenderProps
> = fieldRenderProps => {
  const {
    validationMessage,
    floatingLabel,
    floatLabel,
    placeholder,
    label,
    id = label,
    valid,
    disabled,
    touched,
    hint,
    name,
    ...others
  } = fieldRenderProps;
  const showValidationMessage = touched && validationMessage;
  const hindId = `${id}_hint`;
  const labelId = label ? `${id}_label` : "";
  const errorId = showValidationMessage ? `${id}_error` : "";
  return (
    <FieldWrapper>
      <FormLabel
        id={labelId}
        editorId={id}
        editorValid={valid}
        editorDisabled={disabled}
        floating={floatingLabel}
        floatLabel={floatLabel}
        label={label}
        value={others.value?.toString() || "blank"}
        placeholder={placeholder}
      >
        <NumericTextBox disabled={disabled} name={name} {...others} />
        {hint && <Hint id={hindId}>{hint}</Hint>}
        {showValidationMessage && (
          <Error id={errorId}>{validationMessage}</Error>
        )}
      </FormLabel>
    </FieldWrapper>
  );
};

export const FormMultiSelectTabs = MultiSelectTabs;
